import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './MyWork.css';

const featured = {
  title: 'Vessel',
  subtitle:
    'Designed and developed a privacy-focused self-reflection, feelings, and journaling app that helps users capture daily thoughts, experiences, and personal patterns over time.',
  badges: ['iOS', 'Lifestyle', 'Journaling', 'Self-Reflection'],
  screenshot: '/images/Apple Store封面.PNG',
  appIcon:
    '/images/my upset tracker asset/ChatGPT Image Apr 29, 2026 at 01_43_29 PM.webp',
  appStoreUrl: 'https://apps.apple.com/hk/app/vessel/id6758148342?l=en-GB',
};

const works = [
  {
    id: 1,
    title: 'My Website',
    category: 'Web',
    year: '2025',
    image: '/images/my home page.png',
    link: '/my-work/my-website',
    accent: '#00ff88',
    summary: 'A personal site built to learn React, auth, and i18n.',
  },
  {
    id: 2,
    title: "Climber's Deal ",
    category: 'iOS App',
    year: '2025',
    image: '/images/這是我的鞋子project的圖片/icon origin.png',
    link: '/my-work/climbers-deal',
    iosVariant: 'phone',
    accent: '#ffb347',
    summary: 'A price-alert iOS app for climbers chasing the right deal.',
  },
  {
    id: 3,
    title: 'Game (Working on Game Idea)',
    category: 'Game',
    year: '2026',
    image: '/images/working on idea.webp',
    link: '/my-work/game-idea',
    accent: '#ff7eb6',
    summary: 'A turn-based strategy prototype — still finding its fun.',
    hidden: true,
  },
];

const PAGE_SIZE = 4;
const visibleWorks = works.filter((work) => !work.hidden);

export default function MyWork() {
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const profileLabel = 'Go to my profile';

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const totalPages = Math.max(1, Math.ceil(visibleWorks.length / PAGE_SIZE));

  const pagedWorks = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return visibleWorks.slice(start, start + PAGE_SIZE);
  }, [page]);

  const goPrev = () => {
    if (page > 1) {
      setPage((p) => p - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const goNext = () => {
    if (page < totalPages) {
      setPage((p) => p + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="mywork-overlay">
      {/* Ambient background */}
      <div className="mywork-bg" aria-hidden="true">
        <div className="mywork-bg-glow mywork-bg-glow-a" />
        <div className="mywork-bg-glow mywork-bg-glow-b" />
        <div className="mywork-bg-grid" />
      </div>

      <button
        className="mywork-back"
        onClick={() => navigate('/')}
        aria-label={profileLabel}
      >
        <span className="mywork-back-glow" aria-hidden="true" />
        <span className="mywork-back-avatar" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span className="mywork-back-status" />
        </span>
        <span className="mywork-back-label">{profileLabel}</span>
        <span className="mywork-back-arrow" aria-hidden="true">
          →
        </span>
      </button>

      <div className="mywork-header">
        <h1 className="mywork-title">
          MY <span className="mywork-title-accent">WORK</span>
        </h1>
        <p className="mywork-subtitle">
          Things I&rsquo;ve built — websites, mobile apps, and ideas still in
          progress.
        </p>
      </div>

      {page === 1 && (
        <section
          className={`mywork-featured ${loaded ? 'loaded' : ''}`}
          aria-label="Featured project"
        >
          <div className="mywork-featured-bg" aria-hidden="true">
            <div className="mywork-featured-glow" />
            <div className="mywork-featured-grid" />
          </div>

          {/* Pin icon — Lucide-style outline, sits on top-left corner */}
          <svg
            className="mywork-featured-pin"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label="Pinned"
          >
            <path d="M12 17v5" />
            <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
          </svg>

          {/* Twinkling sparkles to draw the eye */}
          <div className="mywork-featured-stars" aria-hidden="true">
            <span className="mywork-featured-star star-1">✦</span>
            <span className="mywork-featured-star star-2">✦</span>
            <span className="mywork-featured-star star-3">★</span>
            <span className="mywork-featured-star star-4">✧</span>
            <span className="mywork-featured-star star-5">✦</span>
            <span className="mywork-featured-star star-6">✧</span>
          </div>

          <div className="mywork-featured-tag">
            <span className="mywork-featured-tag-spark" aria-hidden="true">
              ✦
            </span>
            FEATURED
          </div>

          <div className="mywork-featured-inner">
            <div className="mywork-featured-visual">
              <div className="mywork-featured-phone">
                <div className="mywork-featured-phone-notch" />
                <img
                  src={featured.screenshot}
                  alt={`${featured.title} app preview`}
                  className="mywork-featured-phone-screen"
                />
              </div>
            </div>

            <div className="mywork-featured-actions">
              <Link
                to="/thevesseldesign"
                className="mywork-featured-cta mywork-featured-cta-secondary"
              >
                <span>View the Design</span>
                <span className="mywork-featured-cta-arrow" aria-hidden="true">
                  →
                </span>
              </Link>
              <a
                href={featured.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mywork-featured-cta"
              >
                <svg
                  className="mywork-featured-cta-apple"
                  viewBox="0 0 384 512"
                  width="14"
                  height="14"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                </svg>
                <span>View on App Store</span>
                <span className="mywork-featured-cta-arrow" aria-hidden="true">
                  ↗
                </span>
              </a>
            </div>

            <div className="mywork-featured-copy">
              <div className="mywork-featured-identity">
                <div className="mywork-featured-name">
                  <h2 className="mywork-featured-title">{featured.title}</h2>
                  <p className="mywork-featured-subtitle">
                    {featured.subtitle}
                  </p>
                </div>
              </div>

              <div className="mywork-featured-badges">
                {featured.badges.map((badge) => (
                  <span key={badge} className="mywork-featured-badge">
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="mywork-grid">
        {pagedWorks.map((work, index) => {
          const inner = (
            <>
              <div className="mywork-card-image">
                {work.image ? (
                  work.category === 'iOS App' ? (
                    work.iosVariant === 'tile' ? (
                      <div className="mywork-ios-tile">
                        <div className="mywork-ios-tile-stack">
                          <span className="mywork-ios-tile-shadow shadow-back" />
                          <span className="mywork-ios-tile-shadow shadow-mid" />
                          <div className="mywork-ios-tile-card">
                            <img
                              src={work.image}
                              alt={work.title}
                              className="mywork-ios-tile-icon"
                            />
                            <div className="mywork-ios-tile-gloss" />
                          </div>
                        </div>
                        <div className="mywork-ios-tile-dock">
                          <span />
                          <span />
                          <span />
                        </div>
                        <span className="mywork-ios-label">iOS APP</span>
                      </div>
                    ) : (
                      <div className="mywork-ios-preview">
                        <div className="mywork-ios-notch" />
                        <img
                          src={work.image}
                          alt={work.title}
                          className="mywork-ios-icon"
                        />
                        <span className="mywork-ios-label">iOS APP</span>
                      </div>
                    )
                  ) : (
                    <img
                      src={work.image}
                      alt={work.title}
                      className="mywork-card-img"
                    />
                  )
                ) : (
                  <span className="mywork-image-placeholder">[ IMAGE ]</span>
                )}
                <span className="mywork-card-corner">
                  {String(work.id).padStart(2, '0')}
                </span>
                <span
                  className="mywork-card-accent-line"
                  style={{ background: work.accent }}
                />
              </div>
              <div className="mywork-card-info">
                <span
                  className="mywork-card-number"
                  style={{ color: work.accent }}
                >
                  / {String(work.id).padStart(2, '0')}
                </span>
                <h2 className="mywork-card-title">{work.title}</h2>
                {work.summary && (
                  <p className="mywork-card-summary">{work.summary}</p>
                )}
                <div className="mywork-card-meta">
                  <span
                    className="mywork-card-tag"
                    style={{
                      color: work.accent,
                      borderColor: `${work.accent}33`,
                      background: `${work.accent}11`,
                    }}
                  >
                    {work.category}
                  </span>
                  <span className="mywork-card-year">{work.year}</span>
                  {work.link && (
                    <span className="mywork-card-cta">
                      View case <span aria-hidden="true">→</span>
                    </span>
                  )}
                </div>
              </div>
            </>
          );

          const cardClass = `mywork-card ${loaded ? 'loaded' : ''}`;
          const cardStyle = {
            transitionDelay: `${index * 0.1}s`,
            '--card-accent': work.accent,
          };

          return work.link ? (
            <Link
              key={work.id}
              to={work.link}
              className={cardClass}
              style={cardStyle}
            >
              {inner}
            </Link>
          ) : (
            <div key={work.id} className={cardClass} style={cardStyle}>
              {inner}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <nav className="mywork-pagination" aria-label="Page navigation">
        <button
          className="mywork-page-btn"
          onClick={goPrev}
          disabled={page === 1}
          aria-label="Previous page"
        >
          <span className="mywork-page-btn-arrow" aria-hidden="true">
            ←
          </span>
          <span>Prev page</span>
        </button>

        <div className="mywork-page-indicator" aria-live="polite">
          <span className="mywork-page-current">
            {String(page).padStart(2, '0')}
          </span>
          <span className="mywork-page-divider" />
          <span className="mywork-page-total">
            {String(totalPages).padStart(2, '0')}
          </span>
        </div>

        <button
          className="mywork-page-btn"
          onClick={goNext}
          disabled={page === totalPages}
          aria-label="Next page"
        >
          <span>Next page</span>
          <span className="mywork-page-btn-arrow" aria-hidden="true">
            →
          </span>
        </button>
      </nav>

      <footer className="mywork-footer">
        <span className="mywork-footer-tag">END OF SELECTION</span>
        <span className="mywork-footer-line" />
        <span className="mywork-footer-note">More projects in progress.</span>
      </footer>
    </div>
  );
}
